import * as React from 'react';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Close';
import { DataGrid, GridColDef, GridRowId } from '@mui/x-data-grid';
import MultiSelectAll from '../../common/widgets/multiSelectAll';
import { Typography, Tooltip, Button } from '@mui/material';
import { useEffect } from 'react';
import AvatarImage from '../../common/avatar-image';
import { useAppSelector } from '../../../redux/hooks';
import EditToolbar from './editToolBar';
import { IusergridProps } from '../type';
import NoRecordsOverlay from '../../common/data-no-records';

export default function UserGrid(props: IusergridProps): JSX.Element {
  const [rows, setRows] = React.useState(props.users);
  const [originalRows, setOriginalRows] = React.useState(props.users);
  const [selections, setSelections] = React.useState<{
    [key: string]: { roles: string[]; departments: string[] };
  }>({});
  const { profileData } = useAppSelector((state) => state.profileData);

  useEffect(() => {
    setRows(props.users);
    setOriginalRows(props.users);
  }, [props.users]);

  const handleEditClick = (id: GridRowId) => () => {
    setRows(rows.map((row) => (row.id === id ? { ...row, edit: true } : row)));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    const originalRow = originalRows.find((row) => row.id === id);
    setRows(rows.map((row) => (row.id === id ? { ...originalRow } : row)));
    setSelections((prevState) => {
      const { [id]: _, ...rest } = prevState;
      return rest;
    });
  };

  const handleSelectionChange =
    (id: GridRowId, field: 'roles' | 'departments') => (selected: string[]) => {
      setSelections((prevState) => ({
        ...prevState,
        [id]: {
          ...prevState[id],
          [field]: selected
        }
      }));

      setRows(
        rows.map((row) => {
          if (row.id === id) {
            if (field === 'roles') {
              if (selected.length === 0) {
                selected.push(
                  props.systemRoles.find(
                    (role) => role.displayName === 'Member'
                  )?.id ?? ''
                );
              }
              return {
                ...row,
                roles: selected.map((roleId) => ({
                  role: {
                    id: roleId,
                    displayName:
                      props.systemRoles.find((role) => role.id === roleId)
                        ?.displayName ?? ''
                  }
                }))
              };
            }
            if (field === 'departments') {
              return {
                ...row,
                departments: selected.map((deptId) => ({
                  department: {
                    id: deptId,
                    displayName:
                      props.systemDepartments.find((dept) => dept.id === deptId)
                        ?.displayName ?? ''
                  }
                }))
              };
            }
          }
          return row;
        })
      );
    };

  const columns: GridColDef[] = [
    {
      field: 'no',
      headerName: 'No',
      width: 27,
      valueGetter: (params) =>
        (params.api.getRowIndex(params.id) as number) + 1,
      sortable: false
    },
    {
      field: 'displayName',
      headerName: 'Name',
      minWidth: 248,
      flex: 1,
      editable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <AvatarImage
            providerId={params.row.providerId}
            alt={params.row.displayName}
          />
          <Tooltip title={params.row.displayName}>
            <Typography>{params.row.displayName}</Typography>
          </Tooltip>
        </Box>
      )
    },
    {
      field: 'jobTitle',
      headerName: 'Designation',
      minWidth: 185,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      editable: false,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={params.row.jobTitle || '   -'}>
          <Typography>
            {params.row.jobTitle ? params.row.jobTitle : '   -'}
          </Typography>
        </Tooltip>
      )
    },
    {
      field: 'roles',
      headerName: 'System Role',
      type: 'multipleSelect',
      minWidth: 216,
      flex: 1,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        const isInEditMode = params.row.edit;
        const selectedRoles =
          selections[params.row.id]?.roles ||
          params.row.roles.map(
            (role: { role: { id: string } }) => role.role.id
          );
        if (isInEditMode) {
          return (
            <MultiSelectAll
              placeholder=""
              menuItems={props.systemRoles.filter(
                (role) =>
                  role.displayName !== 'Project Manager/ Delivery Responsible'
              )}
              selected={selectedRoles}
              setSelected={handleSelectionChange(params.row.id, 'roles')}
            />
          );
        }

        return (
          <Tooltip
            title={
              params.row.roles.length > 0
                ? params.row.roles
                    .map(
                      (role: { role: { id: string; displayName: string } }) =>
                        role.role.displayName
                    )
                    .join(', ')
                : '-'
            }
          >
            <Typography>
              {params.row.roles.length > 0
                ? params.row.roles
                    .map(
                      (role: { role: { id: string; displayName: string } }) =>
                        role.role.displayName
                    )
                    .join(', ')
                : '-'}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      field: 'department',
      headerName: 'Department',
      minWidth: 220,
      flex: 1,
      editable: false,
      type: 'multipleSelect',
      sortable: false,
      renderCell: (params) => {
        const isInEditMode = params.row.edit;
        const selectedDepartments =
          selections[params.row.id]?.departments ||
          params.row.departments.map(
            (department: { department: { id: string } }) =>
              department.department.id
          );

        if (isInEditMode) {
          return (
            <MultiSelectAll
              placeholder=""
              menuItems={props.systemDepartments}
              selected={selectedDepartments}
              setSelected={handleSelectionChange(params.row.id, 'departments')}
            />
          );
        }

        return (
          <Tooltip
            title={
              params.row.departments.length > 0
                ? params.row.departments
                    .map(
                      (department: {
                        department: { id: string; displayName: string };
                      }) => department.department.displayName
                    )
                    .join(', ')
                : '-'
            }
          >
            <Typography>
              {params.row.departments.length > 0
                ? params.row.departments
                    .map(
                      (department: {
                        department: { id: string; displayName: string };
                      }) => department.department.displayName
                    )
                    .join(', ')
                : '-'}
            </Typography>
          </Tooltip>
        );
      }
    }
  ];

  if (profileData?.permissions?.rolesAndDepartments.update) {
    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      renderCell: (params) => {
        const isInEditMode = params.row.edit;
        if (isInEditMode) {
          return (
            <Button color="inherit" onClick={handleCancelClick(params.id)}>
              <CancelIcon sx={{ color: '#EB3939' }} />
            </Button>
          );
        }
        return (
          <Button color="inherit" onClick={handleEditClick(params.id)}>
            <EditIcon sx={{ color: '#2C5DA7' }} />
          </Button>
        );
      }
    });
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        '& .actions': {
          color: 'text.secondary'
        },
        '& .textPrimary': {
          color: 'text.primary'
        },
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: '#E5F2FF'
        }
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        components={{
          Toolbar: EditToolbar,
          NoRowsOverlay: () => (
            <NoRecordsOverlay
              isLoading={props.loading}
              dataLength={rows.length}
              message="No Users Available."
            />
          )
        }}
        componentsProps={{
          toolbar: {
            rows,
            setRows,
            originalRows,
            setOriginalRows,
            selections,
            setSelections
          }
        }}
        disableColumnMenu
        loading={props.loading}
        disableColumnSelector
        pagination
        disableVirtualization
        sx={{
          border: 'none',
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
            width: '7px',
            height: '7px',
            backgroundColor: 'rgba(217, 217, 217, 0.4)'
          },
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(217, 217, 217, 1)',
            borderRadius: '10px'
          },
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#888'
          }
        }}
      />
    </Box>
  );
}
